import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { Subscription } from 'rxjs';

import { ApplicationApiService } from 'src/app/services/applicationApiService';
import { ValidationService } from 'src/app/services/ValidationService';
import { EnumConverterService } from 'src/app/services/EnumConverterService';
import { OptionsEnum } from 'src/app/model/enums/OptionsEnum';
import { SessionService } from 'src/app/session-service.service';
import { CustomerTypeEnum } from 'src/app/model/enums/CustomerTypeEnum';
import { ActivatedRoute, Router } from '@angular/router';
import NavigationSettings, {
  StepsGG,
} from '../../../components/navigation-component/navigation-settings.service';
import { ApplicationStepperLabels } from './application-page.settings';
import NavigationStepperMapper from './NavigationStepperMapper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { ProductTypeEnum } from '../../../model/enums/ProductTypeEnum';
import { SessionGgService } from '../../../services/session-gg.service';
import { KnowledgeTestFormService } from '../knowledge-test/knowledge-test-form.service';
import { ApplicationGGDto } from '../../../model/business/application/ApplicationGGDto';
import { getDutchAddress, openPDFFromByteCharacters, replaceThousandSeparatorAndRemovingEuroPrefix } from '../../../utils/utils';
import { Constants } from '../../../constants';
import { CalculationResponseResultPremiumDto } from '../../../model/business/calculation/CalculationResponseResultPremiumDto';
import { ApplicationTransactionDto } from '../../../model/business/application/ApplicationTransactionDto';
import { QuotationRequestDto } from '../../../model/business/calculation/QuotationRequestDto';
import { CalculationApiService } from '../../../services/calculationApiService';
import { InsuranceDto } from '../../../model/business/application/InsuranceDto';
import { PersonalDetails } from '../../../model/business/application/PersonalDetails';
import { InsuredAttributesDtoExtended } from '../../../model/business/calculation/InsuredAttributesDto';

@Component({
  selector: 'app-application-page-gg',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './application-page.component.html',
  styleUrls: ['./application-page.component.css'],
})
export class ApplicationPageGGComponent implements OnInit, OnDestroy {
  applicationFormGroup: FormGroup;
  productType = ProductTypeEnum.GG;
  isLinear = true;
  form;
  premiums: CalculationResponseResultPremiumDto | undefined;
  resetting: boolean;

  yesNoList = this.enumConverterService.toBaseList('OptionsEnum', OptionsEnum);
  OptionsEnum = OptionsEnum;
  CustomerTypeEnum = CustomerTypeEnum;
  ApplicationStepperLabels = ApplicationStepperLabels;
  showTitle: boolean;
  @ViewChild('stepper') stepper;
  @ViewChild('knowledgeTest') knowledgeTest;
  @ViewChild('productInfo') productInfo;
  @ViewChild('personalDetails') personalDetails;

  relativePath: string;
  premiumSubscription: Subscription;
  applicationDto = new ApplicationGGDto();

  constructor(
    private fb: FormBuilder,
    private applicationApiService: ApplicationApiService,
    private sessionService: SessionService,
    private validationService: ValidationService,
    private enumConverterService: EnumConverterService,
    private router: Router,
    private navigationSettings: NavigationSettings,
    private activatedRoute: ActivatedRoute,
    private sessionGgService: SessionGgService,
    public knowledgeTestFormService: KnowledgeTestFormService,
    private calculationApiService: CalculationApiService,
  ) {
    const {
      productType,
      locale,
    } = this.activatedRoute.snapshot.params;

    this.relativePath = locale ?
      `/${locale}/${productType}` :
      `/${productType}`;

    this.resetting = false;
  }

  ngOnInit(): void {
    this.showTitle = true;
    this.initializeInsuredFormGroup();
    this.updatePremiumValues();
    this.premiumSubscription = this.sessionService.getCalculationResponse.subscribe((premiums) => {
      this.premiums = premiums;
    });
  }

  ngOnDestroy(): void {
    this.premiumSubscription.unsubscribe();
    if (!this.resetting) {
      this.sessionGgService.setInsuredFormValues(this.form.value.insured);
    }
    this.navigationSettings.setActiveState(StepsGG.PRODUCT_INFO, false);
  }

  personalDetailsDestroyHandler(form): void {
    if (!this.resetting) {
      this.sessionGgService.setPersonalDetailsFormValues(form);
    }
  }

  setActiveStep(): void {
    this.navigationSettings.setActiveState(StepsGG.PRODUCT_INFO, true);
  }

  stepperChangeHandler(e: StepperSelectionEvent): void {
    this.navigationSettings.setActiveState(
      NavigationStepperMapper[e.previouslySelectedStep.label],
      false,
    );
    this.navigationSettings.setActiveState(
      NavigationStepperMapper[e.selectedStep.label],
      true,
    );
  }

  initializeInsuredFormGroup(): void {
    const formValues = this.sessionGgService.getInsuredFormValues;
    this.form = this.fb.group({
      insured: this.fb.group({
        initials: new FormControl(formValues.initials),
        middleName: new FormControl(formValues.middleName),
        surname: new FormControl(formValues.surname),
        gender: new FormControl(formValues.gender),
        dateOfBirth: new FormControl(formValues.dateOfBirth),
        zipCode: new FormControl(formValues.zipCode),
        houseNumber: new FormControl(formValues.houseNumber),
        houseNumberSuffix: new FormControl(formValues.houseNumberSuffix),
        street: new FormControl(formValues.street),
        city: new FormControl(formValues.city),
        mobilePhoneNo: new FormControl(formValues.mobilePhoneNo),
        emailAddress: new FormControl(formValues.emailAddress),
        confirmedEmailAddress: new FormControl(formValues.confirmedEmailAddress),
        nationality: new FormControl(formValues.nationality),
        iban: new FormControl(formValues.iban),
        addressSameAsFirstInsured: new FormControl(formValues.addressSameAsFirstInsured),
      })
    });
    this.validationService.setInsuredValidators(
      this.form.get('insured') as FormGroup
    );
  }

  handleNextFromInsured(): void {
    this.form.markAllAsTouched();
    if (this.form.get('insured').status === 'VALID') {
      this.sessionGgService.setInsuredFormValues(this.form.value.insured);
      this.stepper.next();
    }
  }

  handleNextFromProductInfo(): void {
    if (this.productInfo.isValidCalculation) {
      this.stepper.next();
    }
  }

  checkKnowledgePageAndGoNext(pageNumber): void {
    this.knowledgeTest.validatePageForNextStep(pageNumber)
      .then(() => {
        this.stepper.next();
      })
      .catch(() => {
        console.log(`Knowledge test page number ${pageNumber} is not valid`);
      });
  }

  completeTest(): void {
    const isTestPassed = this.knowledgeTestFormService.getIsTestPassed();

    if (isTestPassed) {
      this.stepper.next();
    }
  }

  handleNextFromPersonalDetail(): void {
    if (this.personalDetails.form.status === 'VALID') {
      this.sessionGgService.setPersonalDetailsFormValues(this.personalDetails.form.value);
      this.stepper.next();
    } else {
      this.personalDetails.form.markAllAsTouched();
    }
  }

  updateApplicationDto(): void {
    const { occupation } = this.sessionGgService.getCalculationPageValues();
    const personalDetailsFormValues = this.sessionGgService.getPersonalDetailsFormValues;
    const insuredFormValues = this.sessionGgService.getInsuredFormValues;
    const calculationRequest = this.sessionService.getCallculationRequest();
    const app = new ApplicationGGDto();

    app.insured1 = insuredFormValues;
    app.insured1.address = getDutchAddress(insuredFormValues);
    app.calculationRequest = calculationRequest;

    app.monthlyExpenses = replaceThousandSeparatorAndRemovingEuroPrefix(personalDetailsFormValues.monthlyExpenses, Constants.THOUSAND_SEPARATOR);
    app.averageYearIncome = replaceThousandSeparatorAndRemovingEuroPrefix(personalDetailsFormValues.averageYearIncome, Constants.THOUSAND_SEPARATOR);

    app.insurances = [];

    const insurance = this.getOtherInsurances(personalDetailsFormValues);
    if (insurance) {
      app.insurances.push(insurance);
    }

    app.deductPremiumFromTaxes = personalDetailsFormValues.deductPremiumFromTaxes === OptionsEnum.Yes;
    app.insuranceSomewhereElse = personalDetailsFormValues.insuranceSomewhereElse === OptionsEnum.Yes;
    app.bankAccount.iban = insuredFormValues.iban.replace(/ /g, '');
    app.calculationResponse = this.premiums;
    app.dateOfCommencement = calculationRequest.dateOfCommencement;
    app.insured1Premium = this.premiums.insured1Amount;
    app.intermediary.organisationId = this.sessionService.getIntermediaryId();
    app.insured1.dateOfBirth = calculationRequest.insureds[0].dateOfBirth;
    app.insured1.isSmoker = calculationRequest.insureds[0].isSmoker;
    app.insured1.phoneNumber = `0${insuredFormValues.mobilePhoneNo}`;
    app.insured1.occupation = occupation;
    this.applicationDto = app;
  }

  getOtherInsurances(otherInsurancesFrmGrp: PersonalDetails): InsuranceDto {
    if ( otherInsurancesFrmGrp.insuranceSomewhereElse === OptionsEnum.No ) {
      return null;
    }

    const otherInsuranceDto = new InsuranceDto();

    otherInsuranceDto.insurerHasAnotherInsurer = otherInsurancesFrmGrp.insuranceSomewhereElse === OptionsEnum.Yes;
    otherInsuranceDto.tafInsurer = otherInsurancesFrmGrp.companyName === OptionsEnum.Yes;
    otherInsuranceDto.nameInsurer = otherInsurancesFrmGrp.companyName || 'TAF';
    otherInsuranceDto.sumAssured = replaceThousandSeparatorAndRemovingEuroPrefix(otherInsurancesFrmGrp.sumAssuredAOV);
    otherInsuranceDto.willCancelPolicy =
      otherInsurancesFrmGrp.insurerWillCancelInsurance === OptionsEnum.Yes;

    if (otherInsurancesFrmGrp.insurerWillCancelInsurance === OptionsEnum.Yes) {
      otherInsuranceDto.cancellationDate = otherInsurancesFrmGrp.cancellationDate;
    } else {
      otherInsuranceDto.cancellationDate = null;
    }

    return otherInsuranceDto;
  }

  submitApplication(): void {
    const transactionApplicationDto = new ApplicationTransactionDto();
    transactionApplicationDto.applicationDto = this.applicationDto;
    transactionApplicationDto.merchantReference = '';
    this.applicationApiService
      .sendGGApplicationRequest(transactionApplicationDto)
      .subscribe(() => {
        this.stepper.next();
      });
  }

  updatePremiumValues(): void {
    this.premiumSubscription = this.sessionService.getCalculationResponse.subscribe(value => {
      if (value.premium > 0) {
        this.premiums = value;
      }
    });
  }

  downloadQuote(): void {
    const quotationRequestDto = new QuotationRequestDto();
    const { occupation } = this.sessionGgService.getCalculationPageValues();
    const insuredFormValues = this.sessionGgService.getInsuredFormValues;

    quotationRequestDto.calculationRequestDto = this.sessionService.getCallculationRequest();
    quotationRequestDto.calculationRequestDto.insureds[0].gender = insuredFormValues.gender;
    (quotationRequestDto.calculationRequestDto.insureds[0] as InsuredAttributesDtoExtended).occupation = occupation;
    quotationRequestDto.calculationRequestDto.variables.intermediaryFee = {
      type: 'string',
      value: this.sessionService.intermediaryFee,
    };
    quotationRequestDto.premiumResponse = this.premiums;
    quotationRequestDto.intermediaryName = this.sessionService.intermediaryName;
    quotationRequestDto.intermediaryEmail = this.sessionService.intermediaryEmail;
    quotationRequestDto.intermediaryPhone = this.sessionService.intermediaryPhone;
    this.calculationApiService
      .getQuotation(quotationRequestDto)
      .subscribe((response) => {
        const byteCharacters = response.pdfContent;
        openPDFFromByteCharacters(byteCharacters, 'Offerte.pdf');
      });
  }

  resetApplication(): void {
    this.resetting = true;
    this.navigationSettings.reset();
    this.knowledgeTestFormService.reset();
    this.sessionService.resetCalculationResponse();
    this.sessionGgService.resetApplication();
  }
}
